import { TDynamicVariable } from "@shared/types";
import { IconButton, Menu, MenuItem, styled } from "@mui/material";
import React, { FunctionComponent, useEffect, useMemo } from "react";

import IcIcon from "../../../IcIcon";

const StyledMenu = styled(Menu)`
  box-shadow: 0px 20px 5px rgba(0, 0, 0, 0.7);
`;

const VariableMenu: Array<{
  label: string;
  flag?: string;
  variable: TDynamicVariable;
}> = [
  {
    label: "Number",
    variable: {
      variableType: "NUMBER",
      number: 0,
    },
  },
  {
    label: "Component",
    variable: {
      variableType: "CATEGORY",
      output: "EXISTS",
    },
  },
  {
    label: "Specification",
    variable: {
      variableType: "SUBCATEGORY",
      output: "VALUE",
    },
  },
  {
    label: "Takeoff Variable",
    variable: {
      variableType: "TAKEOFF_VARIABLE",
      takeoffVariable: "linePostDistanceOnCenter",
      output: "VALUE",
    },
  },
  {
    flag: "withStaticSubCategory",
    label: "Static Specification",
    variable: {
      variableType: "STATIC_SUBCATEGORY",
      output: "VALUE",
    },
  },
  {
    label: "Fence",
    variable: {
      variableType: "LINE",
    },
  },
  {
    label: "Post",
    variable: {
      variableType: "POINT",
    },
  },
  {
    label: "Parent Component",
    variable: {
      variableType: "PARENT_CATEGORY",
      output: "EXISTS",
    },
  },
  {
    label: "Parent Specification",
    variable: {
      variableType: "PARENT_SUBCATEGORY",
      output: "VALUE",
    },
  },
  {
    label: "Parent Static Specification",
    variable: {
      variableType: "PARENT_STATIC_SUBCATEGORY",
      output: "VALUE",
    },
  },
];

const StringExtra: Array<{
  label: string;
  variable: TDynamicVariable;
}> = [
  {
    label: "Text",
    variable: {
      variableType: "TEXT",
      text: "",
    },
  },
];

export type MenuFlags = {
  withStaticSubCategory: boolean;
};

export const NewItemButton: FunctionComponent<
  {
    addVariable: (variable: TDynamicVariable) => void;
    disabled?: boolean;
    outputVariant?: "number" | "string" | "both";
    icon?: string;
    enableParentProduct?: boolean;
  } & Partial<MenuFlags>
> = ({
  addVariable,
  disabled,
  outputVariant,
  icon,
  enableParentProduct,
  ...coreFlags
}) => {
  const flags: MenuFlags = {
    withStaticSubCategory: false,
    ...coreFlags,
  };
  const menus = useMemo(() => {
    if (outputVariant === "string" || outputVariant === "both") {
      return [...StringExtra, ...VariableMenu];
    }
    return VariableMenu.filter((menu) => {
      if (menu.flag) {
        return flags[menu.flag as keyof MenuFlags];
      }
      return true;
    });
  }, [outputVariant, JSON.stringify(flags)]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    if (disabled) {
      handleClose();
    }
  }, [disabled, open]);
  return (
    <div>
      <IconButton onClick={handleClick} disabled={disabled}>
        <IcIcon icon={icon || "ant-design:down-circle-outlined"} />
      </IconButton>
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {menus
          .filter((menu) => {
            return /^PARENT/.test(menu.variable.variableType)
              ? enableParentProduct
              : true;
          })
          .map((menu) => {
            const key = JSON.stringify(menu.variable);
            return (
              <MenuItem
                key={key}
                onClick={() => {
                  addVariable(menu.variable);
                  handleClose();
                }}
              >
                {menu.label}
              </MenuItem>
            );
          })}
      </StyledMenu>
    </div>
  );
};
